import React, { useState, useRef } from "react";
import { Modal, Form, Input, Button, message, Spin, Checkbox, Select, Divider, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient, useQuery } from "react-query";
import { createCompanyBranch, getCompanyBranches, editCompanyBranch, getCompany } from "../../../service/client";
import { useBranchContext } from "../../../contexts/branch-context";
import { useJsApiLoader, Autocomplete } from "@react-google-maps/api";
import styled from "styled-components";
import { colors } from "../../../styles/theme";

const { Title, Text } = Typography;

interface CreateBranchModalProps {
  open: boolean;
  onClose: () => void;
}

const libraries: ("places" | "geometry" | "drawing")[] = ["geometry", "places", "drawing"];
const GOOGLE_MAPS_API_KEY = "AIzaSyA3lh6-T9qUYkdo46jFI1jaNQ_z2JNdCEs";

const AddressInputWrapper = styled.div`
  position: relative;

  .pac-container {
    border-radius: 8px;
    border: 1px solid var(--color-border);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    margin-top: 4px;
  }
`;

const SlugPrefix = styled.span`
  color: ${colors.textSecondary};
  font-size: 14px;
  white-space: nowrap;
  user-select: none;
`;

const BranchSlugRow = styled.div`
  margin-bottom: 20px;
`;

const BranchSlugLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${colors.textPrimary};
  margin-bottom: 4px;
`;


const SetupModalDescription = styled.p`
  font-size: 14px;
  color: ${colors.textSecondary};
  margin: 0 0 24px 0;
  line-height: 1.6;
`;


interface BranchSlugsSetupModalProps {
  open: boolean;
  onClose: () => void;
  /** Tylko poprzedni (pierwszy) oddział — bez sluga */
  oldBranch: { id: number; name: string };
  companySlug: string;
}

const BranchSlugsSetupModal: React.FC<BranchSlugsSetupModalProps> = ({
  open,
  onClose,
  oldBranch,
  companySlug,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [slugValue, setSlugValue] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const canSave = slugValue.trim().length >= 3;

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await editCompanyBranch({ id: oldBranch.id, data: { slug: slugValue.trim() } });
      message.success(t("createBranchModal.branchAddressSaved"));
      queryClient.invalidateQueries(["/company-branches"]);
      onClose();
    } catch (error: any) {
      message.error(error?.response?.data?.error || t("createBranchModal.saveAddressError"));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      title={t("createBranchModal.setupModalTitle")}
      open={open}
      closable={false}
      maskClosable={false}
      footer={[
        <Button key="save" type="primary" onClick={handleSave} loading={isSaving} disabled={!canSave}>
          {t("createBranchModal.saveAddressButton")}
        </Button>,
      ]}
      width={520}
      centered
    >
      <SetupModalDescription>
        {t("createBranchModal.setupModalDescription")}
      </SetupModalDescription>

      <BranchSlugRow>
        <BranchSlugLabel>{t("createBranchModal.urlAddressLabel", { name: oldBranch.name })}</BranchSlugLabel>
        <Input
          prefix={<SlugPrefix>proste.menu/{companySlug}/</SlugPrefix>}
          placeholder={t("createBranchModal.slugPlaceholder")}
          value={slugValue}
          onChange={(e) => {
            const normalized = e.target.value.toLowerCase().replace(/\s/g, "-");
            setSlugValue(normalized);
          }}
          autoFocus
        />
      </BranchSlugRow>
    </Modal>
  );
};

export const CreateBranchModal: React.FC<CreateBranchModalProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const { setBranchDirectly } = useBranchContext();
  const [selectedCoordinates, setSelectedCoordinates] = useState<{ lat: number; lng: number } | null>(null);
  const [addressValue, setAddressValue] = useState<string>('');
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const [sourceBranchId, setSourceBranchId] = useState<number | null>(null);
  const [copyOptions, setCopyOptions] = useState({
    copyProducts: false,
    copyDeliveryZones: false,
    copyOpeningHours: false,
  });
  const [setupModalData, setSetupModalData] = useState<{
    oldBranch: { id: number; name: string };
    companySlug: string;
  } | null>(null);

  const { data: branches } = useQuery(["/company-branches"], getCompanyBranches);
  const { data: company } = useQuery(["/company"], getCompany);

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries,
  });

  const handlePlaceChanged = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      if (place.geometry?.location) {
        const components = place.address_components || [];
        const hasStreetNumber = components.some((c: google.maps.GeocoderAddressComponent) => c.types.includes("street_number"));
        const hasPremise = components.some((c: google.maps.GeocoderAddressComponent) => c.types.includes("premise"));
        const hasRoute = components.some((c: google.maps.GeocoderAddressComponent) => c.types.includes("route"));
        if ((!hasStreetNumber && !hasPremise) || (hasRoute && !hasStreetNumber)) {
          setAddressValue('');
          message.warning(t("createBranchModal.fullAddressWarning"));
          return;
        }
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        setSelectedCoordinates({ lat, lng });
        const formattedAddress = place.formatted_address || '';
        setAddressValue(formattedAddress);
      }
    }
  };

  const createMutation = useMutation(
    async (values: any) => {
      const body: any = {
        name: values.name,
        slug: values.slug || null,
        sellType: "PRODUCTS_SELLER",
        isOpen: false,
        qrCodesActive: false,
        allowFutureOrders: false,
        allowCurrentOrders: false,
        acceptsOfflinePayments: false,
        acceptsToTheAddress: false,
        clientsSmsNotificationsActive: false,
        companySmsNotificationsActive: false,
        openingHours: null,
        notificationEmails: [],
        notificationPhoneNumbers: [],
        config: {
          welcome_screen: {
            main_button: {
              text: t("createBranchModal.welcomeMainButton"),
              navigate: "dishes",
            },
            additional_button: {
              text: t("createBranchModal.welcomeAdditionalButton"),
              navigate: "services",
            },
          },
        },
      };

      if (selectedCoordinates) {
        body.geographicPoint = {
          lat: selectedCoordinates.lat,
          lng: selectedCoordinates.lng,
        };
        body.formattedAddress = addressValue;
      }

      if (sourceBranchId) {
        body.copyFromBranchId = sourceBranchId;
        body.copyOptions = copyOptions;
      }

      return await createCompanyBranch(body);
    },
    {
      onSuccess: async (newBranch) => {
        message.success(t("createBranchModal.branchCreated"));

        if (newBranch) {
          setBranchDirectly(newBranch);
        }

        queryClient.invalidateQueries(["/company-branches"]);

        // If this was the 2nd branch (branches.length was 1 before), show slug setup modal for old branch
        const wasFirstBranch = (branches?.length ?? 0) === 1;
        if (wasFirstBranch && company?.slug && branches?.[0]) {
          setSetupModalData({
            companySlug: company.slug,
            oldBranch: { id: branches[0].id, name: branches[0].name },
          });
        }

        form.resetFields();
        setSelectedCoordinates(null);
        setAddressValue('');
        onClose();
      },
      onError: (error: any) => {
        message.error(error?.response?.data?.error || t("createBranchModal.createBranchError"));
      },
    }
  );

  const handleSubmit = async () => {
    const values = await form.validateFields();
    await createMutation.mutateAsync(values);
  };

  const handleCancel = () => {
    form.resetFields();
    setSelectedCoordinates(null);
    setAddressValue('');
    setSourceBranchId(null);
    setCopyOptions({
      copyProducts: false,
      copyDeliveryZones: false,
      copyOpeningHours: false,
    });
    onClose();
  };

  const companySlug = company?.slug || "";

  return (
    <>
      <Modal
        title={t("createBranchModal.modalTitle")}
        open={open}
        onCancel={handleCancel}
        footer={[
          <Button key="cancel" onClick={handleCancel}>
            {t("createBranchModal.cancelButton")}
          </Button>,
          <Button key="submit" type="primary" onClick={handleSubmit} loading={createMutation.isLoading}>
            {t("createBranchModal.createButton")}
          </Button>,
        ]}
        width={600}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 24 }}>
          <Form.Item
            name="name"
            label={t("createBranchModal.nameLabel")}
            rules={[{ required: true, message: t("createBranchModal.nameRequired") }]}
          >
            <Input placeholder={t("createBranchModal.namePlaceholder")} />
          </Form.Item>

          <Form.Item
            name="slug"
            label={t("createBranchModal.slugLabel")}
            extra={companySlug ? t("createBranchModal.slugExtraWithSlug", { companySlug }) : t("createBranchModal.slugExtra")}
            rules={[
              { required: true, message: t("createBranchModal.slugRequired") },
              { pattern: /^[a-z0-9-]+$/, message: t("createBranchModal.slugPattern") },
              { min: 3, message: t("createBranchModal.slugMin") },
              { max: 50, message: t("createBranchModal.slugMax") },
            ]}
            normalize={(value: string) => value?.toLowerCase().replace(/\s/g, "-")}
          >
            <Input
              prefix={companySlug ? <SlugPrefix>proste.menu/{companySlug}/</SlugPrefix> : undefined}
              placeholder={t("createBranchModal.slugPlaceholder")}
            />
          </Form.Item>

          <Form.Item
            label={t("createBranchModal.addressLabel")}
            required
            rules={[
              {
                validator: () => {
                  if (!selectedCoordinates) {
                    return Promise.reject(t("createBranchModal.addressSelectError"));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <AddressInputWrapper>
              {isLoaded ? (
                <Autocomplete
                  onLoad={(autocomplete) => {
                    autocomplete.setFields(["place_id", "geometry", "formatted_address", "address_components"]);
                    autocompleteRef.current = autocomplete;
                  }}
                  onPlaceChanged={handlePlaceChanged}
                  options={{
                    componentRestrictions: { country: 'pl' },
                    types: ['address'],
                  }}
                >
                  <Input
                    placeholder={t("createBranchModal.addressPlaceholder")}
                    value={addressValue}
                    onChange={(e) => setAddressValue(e.target.value)}
                  />
                </Autocomplete>
              ) : (
                <Input
                  placeholder={t("createBranchModal.loadingPlaceholder")}
                  prefix={<Spin size="small" />}
                  disabled
                />
              )}
            </AddressInputWrapper>
          </Form.Item>

          <Divider />

          <Title level={5} style={{ marginBottom: 16 }}>{t("createBranchModal.copySettingsTitle")}</Title>

          <Form.Item label={t("createBranchModal.copySettingsLabel")}>
            <Select
              placeholder={t("createBranchModal.copySettingsPlaceholder")}
              allowClear
              value={sourceBranchId}
              onChange={(value) => {
                setSourceBranchId(value);
                if (!value) {
                  setCopyOptions({
                    copyProducts: false,
                    copyDeliveryZones: false,
                    copyOpeningHours: false,
                  });
                }
              }}
            >
              {branches?.map((branch: any) => (
                <Select.Option key={branch.id} value={branch.id}>
                  {branch.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          {sourceBranchId && (
            <Form.Item style={{ marginBottom: 8 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <Checkbox
                  checked={copyOptions.copyProducts}
                  onChange={(e) => setCopyOptions({ ...copyOptions, copyProducts: e.target.checked })}
                >
                  {t("createBranchModal.copyProducts")}
                </Checkbox>
                <Checkbox
                  checked={copyOptions.copyDeliveryZones}
                  onChange={(e) => setCopyOptions({ ...copyOptions, copyDeliveryZones: e.target.checked })}
                >
                  {t("createBranchModal.copyDeliveryZones")}
                </Checkbox>
                <Checkbox
                  checked={copyOptions.copyOpeningHours}
                  onChange={(e) => setCopyOptions({ ...copyOptions, copyOpeningHours: e.target.checked })}
                >
                  {t("createBranchModal.copyOpeningHours")}
                </Checkbox>
              </div>
            </Form.Item>
          )}
        </Form>
      </Modal>

      {setupModalData && (
        <BranchSlugsSetupModal
          open={true}
          onClose={() => setSetupModalData(null)}
          oldBranch={setupModalData.oldBranch}
          companySlug={setupModalData.companySlug}
        />
      )}
    </>
  );
};
